
import { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChatItem } from "./ChatItem";
import { type TranslationKey } from "@/utils/translations";

interface SavedChat {
  id: string;
  title: string;
  content: string;
  created_at: string;
  is_favorite: boolean;
  is_processed?: boolean;
  updated_at: string;
  user_id?: string;
}

interface ChatDateGroupProps {
  date: string;
  chats: SavedChat[];
  t: Record<TranslationKey, string>;
  onToggleFavorite: (chatId: string) => void;
  isToday?: boolean;
  isYesterday?: boolean;
}

export const ChatDateGroup = ({ 
  date, 
  chats, 
  t, 
  onToggleFavorite, 
  isToday = false, 
  isYesterday = false 
}: ChatDateGroupProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const getDateLabel = () => {
    if (isToday) return "Hoy";
    if (isYesterday) return "Ayer";
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const getDayName = () => {
    if (isToday || isYesterday) return "";
    return format(new Date(date), 'EEEE', { locale: es });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="mb-8">
        {/* Collapsible Date Header */}
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-3 mb-4 px-2 cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors">
            <div className="flex items-center gap-2">
              <ChevronDown 
                className={`h-5 w-5 text-coolGray transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : 'rotate-0'
                }`} 
              />
              <h2 className="text-lg font-semibold text-navy">
                {getDateLabel()}
              </h2>
              {!isToday && !isYesterday && (
                <span className="text-sm text-coolGray capitalize">
                  {getDayName()}
                </span>
              )}
            </div>
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-coolGray bg-gray-50 px-2 py-1 rounded-full">
              {chats.length} {chats.length === 1 ? 'conversación' : 'conversaciones'}
            </span>
          </div>
        </CollapsibleTrigger>

        {/* Collapsible Content - Chats for this date */}
        <CollapsibleContent className="space-y-4">
          <div style={{ gap: '1rem', display: 'flex', flexDirection: 'column' }}>
            {chats.map((chat) => (
              <ChatItem 
                key={chat.id} 
                chat={chat} 
                t={t} 
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
